// Public account entry point — /account/sign-in, /account/sign-up,
// /account/forgot-password, /account/reset-password?token=…,
// /account/verify?token=…
//
// One shell, five forms, one design language. Keeps the auth surface
// coherent and easy to maintain.

import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/homepage/Header";
import { Footer } from "@/components/homepage/Footer";
import { useAuth } from "@/context/AuthContext";
import * as authApi from "@/services/authService";
import brand from "@/config/brand";
import Turnstile from "@/components/Turnstile";

const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();

// Backend → user-facing Arabic. Order matters: 429 first (rate-limit),
// 400 CAPTCHA-related second, then generic fallback.
const captchaMessageFor = (err) => {
  const s = err?.status;
  const body = String(err?.message || "").toLowerCase();
  if (s === 429) return "طلبات كثيرة في وقت قصير. أعد المحاولة بعد قليل.";
  if (s === 503 && body.includes("captcha")) return "تعذّر التحقّق من CAPTCHA حالياً. حاول مجدّداً.";
  if (s === 400 && body.includes("captcha required")) return "الرجاء إكمال التحقّق من الروبوتات أولاً.";
  if (s === 400 && body.includes("captcha failed"))   return "فشل التحقّق. أعد المحاولة.";
  return null;
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const Shell = ({ eyebrow, title, subtitle, footer, testId, children }) => (
  <div className="min-h-screen bg-surface-50 text-ink-900">
    <Header />
    <main data-testid={testId} className="mx-auto flex max-w-xl flex-col px-6 py-14 lg:px-8 lg:py-20">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-intel-800">{eyebrow}</p>
      <h1 className="mt-2 text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-ink-500">{subtitle}</p>}
      <div className="mt-8">{children}</div>
      {footer && <div className="mt-6 text-sm text-ink-500">{footer}</div>}
    </main>
    <Footer />
  </div>
);

const Field = ({ label, testId, error, children }) => (
  <label className="block">
    <span className="text-[12px] font-medium text-ink-700">{label}</span>
    <div className="mt-1.5">{children}</div>
    {error && <span data-testid={`${testId}-error`} className="mt-1 block text-xs text-red-600">{error}</span>}
  </label>
);

const Submit = ({ children, loading, testId }) => (
  <button
    type="submit" disabled={loading} data-testid={testId}
    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-intel-800 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-intel-700 disabled:cursor-not-allowed disabled:opacity-70"
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />}
    {children}
  </button>
);

const Input = (props) => (
  <input
    {...props}
    className="block w-full rounded-md border border-surface-200 bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-intel-500 focus:outline-none focus:ring-2 focus:ring-intel-100"
  />
);

/* ─── Sign In ─── */
export function SignInPage() {
  const nav = useNavigate();
  const { user, login, isLoading } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/";

  if (!isLoading && user) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await login(form.email.trim().toLowerCase(), form.password, form.remember);
      nav(from, { replace: true });
    } catch (e2) {
      if (e2?.status === 429) setErr("تمّ حظر الحساب مؤقّتاً بعد عدة محاولات فاشلة. جرّب لاحقاً.");
      else if (e2?.status === 401) setErr("البريد أو كلمة المرور غير صحيحة.");
      else setErr("تعذّر الوصول إلى الخادم. حاول لاحقاً.");
    } finally { setBusy(false); }
  };

  return (
    <Shell
      eyebrow="Sign in" title="تسجيل الدخول"
      subtitle={`مرحباً بعودتك إلى ${brand.name}.`}
      testId="sign-in-page"
      footer={<>ليس لديك حساب؟ <Link to="/account/sign-up" data-testid="sign-in-to-signup" className="font-medium text-intel-800 underline">أنشئ حساباً</Link></>}
    >
      <form onSubmit={onSubmit} data-testid="sign-in-form" className="space-y-4">
        <Field label="البريد الإلكتروني" testId="sign-in-email">
          <Input type="email" required data-testid="sign-in-email" value={form.email}
                 onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
        </Field>
        <Field label="كلمة المرور" testId="sign-in-password">
          <Input type="password" required minLength={1} data-testid="sign-in-password" value={form.password}
                 onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
        </Field>
        <div className="flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" data-testid="sign-in-remember" checked={form.remember}
                   onChange={(e) => setForm(f => ({ ...f, remember: e.target.checked }))}
                   className="h-4 w-4 rounded border-surface-200 text-intel-800 focus:ring-intel-200" />
            تذكّرني
          </label>
          <Link to="/account/forgot-password" data-testid="sign-in-forgot" className="text-xs text-intel-800 underline">نسيت كلمة المرور؟</Link>
        </div>
        {err && <div data-testid="sign-in-error" role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}
        <Submit loading={busy} testId="sign-in-submit">{busy ? "جاري التحقّق…" : "تسجيل الدخول"}</Submit>
      </form>
    </Shell>
  );
}

/* ─── Sign Up ─── */
export function SignUpPage() {
  const nav = useNavigate();
  const { user, register, isLoading } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [errs, setErrs] = useState({}); const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  if (!isLoading && user) return <Navigate to="/onboarding" replace />;

  const captchaRequired = !!TURNSTILE_SITE_KEY;

  const validate = () => {
    const e = {};
    if (!f.name.trim()) e.name = "الاسم مطلوب";
    if (!emailOk(f.email)) e.email = "بريد إلكتروني غير صالح";
    if (f.password.length < 8) e.password = "8 أحرف على الأقل";
    if (captchaRequired && !captchaToken) e.captcha = "الرجاء إكمال التحقّق من الروبوتات.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrs(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await register(f.email.trim().toLowerCase(), f.password, f.name.trim(), captchaToken);
      toast.success("مرحباً بك في " + brand.name);
      nav("/onboarding", { replace: true });
    } catch (e2) {
      const capMsg = captchaMessageFor(e2);
      if (capMsg) {
        setErrs({ captcha: capMsg });
        setCaptchaToken("");
        setCaptchaReset((n) => n + 1);
      } else if (e2?.status === 409) {
        setErrs({ email: "هذا البريد مسجّل مسبقاً" });
      } else {
        setErrs({ form: "تعذّر إنشاء الحساب الآن. حاول مجدّداً." });
        // Tokens are one-shot; force a fresh challenge for the retry.
        setCaptchaToken("");
        setCaptchaReset((n) => n + 1);
      }
    } finally { setBusy(false); }
  };

  return (
    <Shell
      eyebrow="Sign up" title="إنشاء حساب"
      subtitle={brand.tagline}
      testId="sign-up-page"
      footer={<>لديك حساب بالفعل؟ <Link to="/account/sign-in" data-testid="sign-up-to-signin" className="font-medium text-intel-800 underline">تسجيل الدخول</Link></>}
    >
      <form onSubmit={onSubmit} data-testid="sign-up-form" className="space-y-4">
        <Field label="الاسم الكامل" testId="sign-up-name" error={errs.name}>
          <Input type="text" required data-testid="sign-up-name" value={f.name}
                 onChange={(e) => setF(v => ({ ...v, name: e.target.value }))} />
        </Field>
        <Field label="البريد الإلكتروني" testId="sign-up-email" error={errs.email}>
          <Input type="email" required data-testid="sign-up-email" value={f.email}
                 onChange={(e) => setF(v => ({ ...v, email: e.target.value }))} />
        </Field>
        <Field label="كلمة المرور (8 أحرف على الأقل)" testId="sign-up-password" error={errs.password}>
          <Input type="password" required minLength={8} data-testid="sign-up-password" value={f.password}
                 onChange={(e) => setF(v => ({ ...v, password: e.target.value }))} />
        </Field>

        <div>
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            resetKey={captchaReset}
            onToken={(t) => { setCaptchaToken(t); if (t) setErrs((x) => ({ ...x, captcha: undefined })); }}
            onExpire={() => setCaptchaToken("")}
            onError={() => setCaptchaToken("")}
            testId="sign-up-turnstile"
          />
          {errs.captcha && (
            <div data-testid="sign-up-captcha-error" role="alert"
                 className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {errs.captcha}
            </div>
          )}
        </div>

        {errs.form && <div data-testid="sign-up-error" role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{errs.form}</div>}
        <Submit loading={busy} testId="sign-up-submit">{busy ? "جاري الإنشاء…" : "إنشاء الحساب"}</Submit>
        <p className="mt-3 text-[11px] leading-relaxed text-ink-500">
          بإنشاء حساب أنت توافق على <Link to="/terms" className="underline">شروط الاستخدام</Link>{" "}
          و<Link to="/privacy" className="underline">سياسة الخصوصية</Link>.
        </p>
      </form>
    </Shell>
  );
}

/* ─── Forgot Password ─── */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState(""); const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const captchaRequired = !!TURNSTILE_SITE_KEY;

  const onSubmit = async (e) => {
    e.preventDefault(); setErr("");
    if (captchaRequired && !captchaToken) {
      setErr("الرجاء إكمال التحقّق من الروبوتات أولاً.");
      return;
    }
    setBusy(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase(), captchaToken);
      setSent(true);
    } catch (e2) {
      const capMsg = captchaMessageFor(e2);
      if (capMsg) {
        setErr(capMsg);
        setCaptchaToken("");
        setCaptchaReset((n) => n + 1);
      } else {
        // Do NOT leak account-enumeration even on unknown backend errors:
        // treat every other failure as a "success" from the UI's POV.
        setSent(true);
      }
    } finally { setBusy(false); }
  };
  return (
    <Shell eyebrow="Reset password" title="نسيت كلمة المرور" testId="forgot-password-page"
      subtitle="أدخل بريدك الإلكتروني وسنُرسل لك رابطاً لإعادة تعيين كلمة المرور."
      footer={<Link to="/account/sign-in" className="font-medium text-intel-800 underline">العودة إلى تسجيل الدخول</Link>}>
      {sent ? (
        <div data-testid="forgot-password-sent" className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          إذا كان هذا البريد مسجّلاً لدينا، فقد أُرسل إليه رابط لإعادة التعيين خلال دقائق.
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="forgot-password-form" className="space-y-4">
          <Field label="البريد الإلكتروني" testId="forgot-email">
            <Input type="email" required data-testid="forgot-email" value={email}
                   onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            resetKey={captchaReset}
            onToken={(t) => { setCaptchaToken(t); if (t) setErr(""); }}
            onExpire={() => setCaptchaToken("")}
            onError={() => setCaptchaToken("")}
            testId="forgot-turnstile"
          />
          {err && (
            <div data-testid="forgot-error" role="alert"
                 className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {err}
            </div>
          )}
          <Submit loading={busy} testId="forgot-submit">إرسال رابط الإعادة</Submit>
        </form>
      )}
    </Shell>
  );
}

/* ─── Reset Password (?token=…) ─── */
export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const onSubmit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await authApi.resetPassword(token, pw);
      toast.success("تمّت إعادة تعيين كلمة المرور. مرحباً بك مجدّداً.");
      nav("/", { replace: true });
    } catch (e2) {
      setErr(e2?.status === 400 ? "الرابط غير صالح أو منتهي." : "تعذّر تنفيذ الطلب الآن.");
    } finally { setBusy(false); }
  };
  return (
    <Shell eyebrow="Reset password" title="اختر كلمة مرور جديدة" testId="reset-password-page">
      {!token ? (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">الرابط غير مكتمل.</div>
      ) : (
        <form onSubmit={onSubmit} data-testid="reset-password-form" className="space-y-4">
          <Field label="كلمة المرور الجديدة (8 أحرف على الأقل)" testId="reset-pw">
            <Input type="password" required minLength={8} data-testid="reset-pw" value={pw}
                   onChange={(e) => setPw(e.target.value)} />
          </Field>
          {err && <div data-testid="reset-error" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}
          <Submit loading={busy} testId="reset-submit">تأكيد كلمة المرور الجديدة</Submit>
        </form>
      )}
    </Shell>
  );
}

/* ─── Verify Email (?token=…) ─── */
export function VerifyEmailPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ status: "pending", detail: "" });
  useEffect(() => {
    if (!token) { setState({ status: "invalid", detail: "الرابط غير مكتمل." }); return; }
    (async () => {
      try {
        await authApi.verifyEmail(token);
        if (refreshUser) await refreshUser();
        setState({ status: "ok", detail: "تمّ تأكيد بريدك الإلكتروني بنجاح." });
      } catch (e) {
        setState({ status: "error", detail: e?.status === 400 ? "الرابط غير صالح أو منتهي." : "تعذّر التحقّق." });
      }
    })();
  }, [token, refreshUser]);
  return (
    <Shell eyebrow="Verify email" title="تأكيد البريد الإلكتروني" testId="verify-email-page"
      footer={<Link to="/" className="font-medium text-intel-800 underline">الذهاب إلى الصفحة الرئيسية</Link>}>
      <div className={`rounded-md px-4 py-3 text-sm ${state.status === "ok" ? "bg-emerald-50 text-emerald-800" : state.status === "pending" ? "bg-surface-50 text-ink-700" : "bg-red-50 text-red-700"}`}
           data-testid={`verify-status-${state.status}`}>
        {state.status === "pending" ? "جاري التحقّق…" : state.detail}
      </div>
    </Shell>
  );
}
